import React from 'react';
import {
  FmActionCardGrid,
  type FmActionCardIconKey,
  type FmActionCardTone,
} from './FmActionCardGrid';
import { FmSection } from './sections/FmSection';
import { regionOwnsItsHeading } from './FmOperationalWorkspaceRegions.helpers';
import type {
  FmOperationalRegionDescriptor,
  FmOperationalDestinationPurpose,
  FmOperationalWorkspaceDescriptor,
} from './FmOperationalWorkspaceRoot.types';

type RegionOf<Kind extends FmOperationalRegionDescriptor['kind']> = Extract<
  FmOperationalRegionDescriptor,
  { kind: Kind }
>;

/**
 * The region band owns tone, padding and width; the embedded section's tone
 * only tells its primitives which contrast they sit on. A section that names
 * its own heading takes the region's heading level.
 */
function SectionRegionSurface({ region }: { region: RegionOf<'section'> }): React.ReactElement {
  const section =
    region.bandTone !== undefined && region.bandTone !== 'light' && !region.section.tone
      ? { ...region.section, tone: region.bandTone }
      : region.section;
  return (
    <FmSection
      section={section}
      presentation="embedded"
      headingLevel={regionOwnsItsHeading(region) ? 3 : 4}
    />
  );
}

export function RegionSurface({
  descriptor,
  region,
}: {
  descriptor: FmOperationalWorkspaceDescriptor;
  region: FmOperationalRegionDescriptor;
}): React.ReactElement {
  switch (region.kind) {
    case 'section':
      return <SectionRegionSurface region={region} />;
    case 'handoff':
      return <HandoffRegionSurface descriptor={descriptor} region={region} />;
  }
}

function HandoffRegionSurface({
  descriptor,
  region,
}: {
  descriptor: FmOperationalWorkspaceDescriptor;
  region: RegionOf<'handoff'>;
}): React.ReactElement {
  const presentation: Record<
    FmOperationalDestinationPurpose,
    {
      iconKey: FmActionCardIconKey;
      tone: FmActionCardTone;
      ctaVariant: 'primary' | 'bordered';
    }
  > = {
    implementation: {
      iconKey: 'currency',
      tone: 'accent',
      ctaVariant: 'primary',
    },
    analysis: { iconKey: 'analysis', tone: 'sky', ctaVariant: 'primary' },
    measurement: {
      iconKey: 'measurement',
      tone: 'teal',
      ctaVariant: 'bordered',
    },
    market: { iconKey: 'currency', tone: 'amber', ctaVariant: 'bordered' },
    overview: { iconKey: 'overview', tone: 'sky', ctaVariant: 'bordered' },
    assistant: { iconKey: 'assistant', tone: 'rose', ctaVariant: 'bordered' },
  };
  return (
    <FmActionCardGrid
      columns={2}
      data-testid={`${region.id}-destinations`}
      items={region.destinations.map((destination) => {
        const action = descriptor.controls.find((item) => item.id === destination.actionId);
        if (!action) {
          throw new Error(
            `Handoff destination "${destination.id}" names control "${destination.actionId}", which the workspace does not declare.`,
          );
        }
        const card = destination.purpose ? presentation[destination.purpose] : null;
        return {
          key: destination.id,
          iconKey: card?.iconKey,
          tone: card?.tone,
          label: destination.label,
          description: destination.outcome,
          tags: destination.ownerLabel ? [destination.ownerLabel] : undefined,
          statusLabel: destination.availabilityLabel,
          'data-testid': `operational-destination-${destination.id}`,
          cta: {
            label: action.label,
            variant: card?.ctaVariant,
            onClick: () =>
              descriptor.onEvent?.({
                kind: 'action',
                actionId: action.id,
                expectedRevisionId: action.expectedRevisionId,
              }),
          },
        };
      })}
    />
  );
}
